import React from "react";

// Button - Flexible button component with variants and sizes
export const Button = ({
  children,
  className = "",
  variant = "primary",
  size = "medium",
  as = "button",
  disabled = false,
  ...props
}) => {
  const variants = {
    primary:
      "bg-[var(--color-secondary)] text-[var(--color-primary)] hover:text-[var(--color-secondary)] hover:bg-[var(--color-primary)] hover:brightness-110 hover:shadow-lg hover:scale-[1.02] font-semibold transition-transform duration-200 ease-in-out",
    secondary:
      "bg-[var(--color-highlight-yellow)] text-black hover:bg-yellow-400 hover:shadow-lg hover:scale-[1.02] font-semibold transition-transform duration-200 ease-in-out",
    primaryInverse:
      "bg-[var(--color-primary)] text-[var(--color-secondary)] hover:bg-[var(--color-secondary)] hover:text-[var(--color-primary)] hover:shadow-lg hover:scale-[1.02] font-semibold transition-transform duration-200 ease-in-out",
    outline:
      "bg-transparent text-[var(--color-secondary)] border-2 border-[var(--color-secondary)] hover:bg-[var(--color-primary)] hover:text-white hover:shadow-md hover:scale-[1.02] focus:bg-transparent focus:text-[var(--color-secondary)] focus:shadow-none focus:scale-100 font-semibold transition-transform duration-200 ease-in-out",
    ghost:
      "bg-transparent text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:bg-opacity-10 hover:scale-[1.02] font-semibold transition-transform duration-200 ease-in-out",
    ghostSecondary:
      "bg-transparent text-[var(--color-secondary)]  hover:bg-opacity-10 hover:scale-[1.02] font-semibold transition-transform duration-200 ease-in-out",
    secondaryBg:
      "bg-[var(--color-secondary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-[var(--color-secondary)] hover:shadow-lg hover:scale-[1.02] font-semibold transition-transform duration-200 ease-in-out",
  };

  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 sm:text-lg text-md",
    largeNoPadding: "px-0 py-4 sm:text-lg text-md",
    xl: "px-8 py-4 sm:text-xl text-lg",
  };

  const Component = as;

  // Build the complete className
  const baseClasses = `
    ${variants[variant]}
    ${sizes[size]}
    rounded-sm text-center inline-flex items-center justify-center
    active:scale-[0.98] active:transition-transform active:duration-75
    ${
      disabled
        ? "opacity-50 cursor-not-allowed hover:scale-100 hover:shadow-none hover:brightness-100"
        : "cursor-pointer"
    }
    ${className}
  `
    .replace(/\s+/g, " ")
    .trim();

  // Handle disabled state
  const componentProps = {
    ...props,
    className: baseClasses,
    disabled: as === "button" ? disabled : undefined,
    "aria-disabled": disabled,
  };

  return <Component {...componentProps}>{children}</Component>;
};

// Icon Button - Button with icon support
export const IconButton = ({
  children,
  icon,
  iconPosition = "left",
  iconSize = "sm",
  ...props
}) => {
  const iconSizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const spacing = children ? (iconPosition === "left" ? "mr-2" : "ml-2") : "";

  const renderIcon = () => {
    if (typeof icon === "string") {
      // If icon is a string, render as text (for simple icons like arrows)
      return (
        <span className={`${iconSizes[iconSize]} ${spacing}`}>{icon}</span>
      );
    }

    // If icon is a component, render it
    return icon ? (
      <span className={`${iconSizes[iconSize]} ${spacing}`}>{icon}</span>
    ) : null;
  };

  return (
    <Button {...props}>
      {iconPosition === "left" && renderIcon()}
      {children}
      {iconPosition === "right" && renderIcon()}
    </Button>
  );
};

export default Button;
